// Single source of truth for the five collapsible groups `EventForm` renders
// through `EventFormSection`, and for which validation-error key belongs to
// which group. Keeping the map here (not scattered per section component) is
// what lets a folded section auto-expand and flag itself on a failed submit
// without every section re-deriving "do I own this field".

export type EventFormSectionId = 'basics' | 'content' | 'audience' | 'appearance' | 'links';

export const EVENT_FORM_SECTION_IDS: readonly EventFormSectionId[] = [
    'basics',
    'content',
    'audience',
    'appearance',
    'links'
];

// Keep in step with the keys `validateEventDraft` (eventValidation.ts) can
// produce - every one of them must resolve to a section here.
const FIELD_SECTION_MAP: Record<string, EventFormSectionId> = {
    showFromLocal: 'basics',
    startsAtLocal: 'basics',
    durationHours: 'basics',
    title: 'content',
    subtitle: 'content',
    topic: 'content',
    badgeLabel: 'content',
    actionLabel: 'content',
    practices: 'audience',
    themePreset: 'appearance',
    zoomUrl: 'links'
};

/** Every section id that owns at least one field currently in `errors`. */
export function sectionIdsWithErrors(errors: Record<string, string>): Set<EventFormSectionId> {
    const ids = new Set<EventFormSectionId>();
    for (const field of Object.keys(errors)) {
        const sectionId = FIELD_SECTION_MAP[field];
        if (sectionId) ids.add(sectionId);
    }
    return ids;
}

/** Whether `sectionId` should show its header's error marker. Gated on
 *  `touched` to match every field-level error in the form, which only render
 *  post first-edit (see `set` in EventForm.tsx). */
export function sectionHasError(
    errors: Record<string, string>,
    touched: boolean,
    sectionId: EventFormSectionId
): boolean {
    if (!touched) return false;
    return sectionIdsWithErrors(errors).has(sectionId);
}

/** Open state after a failed submit: nothing already open collapses, and
 *  every section that owns an errored field opens too. */
export function expandSectionsWithErrors(
    open: Record<EventFormSectionId, boolean>,
    errors: Record<string, string>
): Record<EventFormSectionId, boolean> {
    const withErrors = sectionIdsWithErrors(errors);
    if (withErrors.size === 0) return open;
    const next = { ...open };
    for (const id of withErrors) next[id] = true;
    return next;
}
